"use client";

/* ————— The instrument (product proof) —————
   Alternating feature rows, ONE framed iPhone per row at large scale.
   History: v6.0 had three phones side-by-side (each too small to read
   on mobile — Jay's note); v6.1 tried bare cropped panels (legible but
   not clean enough — Jay's note). v6.2 synthesis per the app-screenshot
   research: device frames anchor trust/context, so keep the frame, and
   solve legibility with scale — one large phone per row instead of
   three miniatures. Sources: 2026 App Store conversion guides + the
   Linear floating-panel pattern (docs-research/award-redesign-brief.md).
   Images are the real product-peek screenshots with the hardware frame
   baked in (public/product/{chat,study,plans}.webp) — never draw a CSS
   bezel on top.
*/

import Image from "next/image";
import { Scrub } from "./Scrub";

/* Screenshots captured live from the production app in the simulator
   (2026-07-10 agent run — see scratchpad app-shots/REPORT.md). All four
   carry one coherent thread — Philippians 4:6 and the anxious mind, the
   same verse the hero and showpiece hold. Framed by PIL composite into
   the product-peek bezel (screen rect 80,90→824,1700, r62). */
const FEATURES = [
  {
    rubric: "Ask",
    title: "Ask a question, get an answer grounded in the text.",
    body:
      "Not a vibe, not a hot take. An answer that quotes the passage it stands on, so you can open the reference and check it yourself.",
    image: "/product/v2-ask.webp",
    alt: "Sola chat answering “What's a good verse for going through a tough time?” with Isaiah 41:10 quoted and cited",
  },
  {
    rubric: "Study",
    title: "Every verse comes with its world.",
    body:
      "The narrative around the passage, the key insight, and the moment in history, all in the same place you read.",
    image: "/product/v2-study.webp",
    alt: "Sola study view of Philippians 4:6 with narrative, key insight, and timeline",
  },
  {
    rubric: "Word study",
    title: "Tap a word. Meet the original.",
    body:
      "Every Greek and Hebrew word opens a full lexicon: its primary meaning, what it's doing in this verse, and everywhere else it appears in Scripture.",
    image: "/product/v2-lexicon.webp",
    alt: "Sola lexicon for merimnate (μεριμνάω) showing primary meaning “to worry” and every New Testament use",
  },
  {
    rubric: "Plan",
    title: "Plans built for what you're walking through.",
    body:
      "Tell Sola what you're carrying and it builds a short, focused plan: daily passages chosen for that exact season, not another streak to lose.",
    image: "/product/v2-plan.webp",
    alt: "Sola reading plan “Quiet the Anxious Mind” with five daily passages from Matthew 6 to John 14",
  },
];

export default function ProductPanels() {
  return (
    <section
      data-folio="fol. 3r"
      data-folio-title="The instrument"
      className="relative py-28 sm:py-40"
    >
      <div className="container">
        <Scrub from="up" className="max-w-2xl mb-16 sm:mb-24">
          <p className="rubric mb-4">The instrument</p>
          <h2 className="text-balance">Built like an instrument, not a feed.</h2>
        </Scrub>

        <div className="flex flex-col gap-20 sm:gap-28">
          {FEATURES.map((f, i) => {
            const panelRight = i % 2 === 0;
            return (
              <div
                key={f.rubric}
                className={`grid items-center gap-8 sm:gap-12 lg:gap-20 md:grid-cols-2`}
              >
                {/* Copy */}
                <Scrub from="up" className={panelRight ? "md:order-1" : "md:order-2"}>
                  <p className="rubric mb-3">{f.rubric}</p>
                  <h3 className="text-balance mb-4">{f.title}</h3>
                  <p className="text-muted-foreground leading-relaxed max-w-md">{f.body}</p>
                </Scrub>

                {/* UI panel — large, legible crop of the real screen */}
                <Scrub
                  from={panelRight ? "right" : "left"}
                  className={`${panelRight ? "md:order-2" : "md:order-1"} mx-auto w-full max-w-[21rem] sm:max-w-[23rem] lg:max-w-[25rem]`}
                >
                  <Image
                    src={f.image}
                    alt={f.alt}
                    width={900}
                    height={1773}
                    sizes="(max-width: 768px) 84vw, 400px"
                    className="w-full h-auto drop-shadow-[0_45px_70px_rgba(0,0,0,0.7)]"
                  />
                </Scrub>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
